// Slice 12 — BesideU Admin audit log tab (DESIGN §6.6). State-changing actions
// across all tenants, newest first. Server returns up to `limit` newest entries;
// client-side filters (action group, free-text search) narrow what's visible.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import type { AdminAuditAction, AdminAuditEntry } from '@besideu/shared/admin';
import { api } from '@/lib/api.ts';

type ActionGroup = 'all' | 'org' | 'prompt' | 'policy' | 'cgc' | 'admin' | 'governance' | 'qa';

// Action → group. Inverted from the obvious group → action[] shape so we can
// enforce — via the `satisfies` below — that every `AdminAuditAction` value
// has a home. New audit actions added in `@besideu/shared/admin` will fail
// typecheck here if they aren't categorised, instead of silently disappearing
// from every group filter.
const ACTION_TO_GROUP = {
  Org_Suspended: 'org',
  Org_Reactivated: 'org',
  Safety_Contact_Reassigned: 'org',
  Safety_Contact_Nomination_Cancelled: 'org',
  Pending_Request_Approved: 'org',
  Pending_Request_Rejected: 'org',
  Org_Settings_Updated: 'org',
  Org_Created: 'org',
  Org_HR_Admin_Invited: 'org',
  Org_HR_Admin_InviteResent: 'org',
  PromptDraft_Created: 'prompt',
  PromptDraft_Revised: 'prompt',
  PromptDraft_Edited: 'prompt',
  Policy_Reingest: 'policy',
  Policy_Audit_Requeue: 'policy',
  CGC_Member_Suspended: 'cgc',
  CGC_Member_Reactivated: 'cgc',
  CGC_Member_Invited: 'cgc',
  CGC_Member_Edited: 'cgc',
  CGC_Member_InviteResent: 'cgc',
  CGC_Member_Revoked: 'cgc',
  CGC_Member_Identity_Updated: 'cgc',
  CGC_Member_Promoted_To_Active: 'cgc',
  Admin_Invited: 'admin',
  Admin_InviteResent: 'admin',
  Admin_Suspended: 'admin',
  Admin_Reactivated: 'admin',
  Governance_Role_Assigned: 'governance',
  Governance_Setting_Updated: 'governance',
  QA_Sample_Created: 'qa',
  QA_Redaction_Approved: 'qa',
  QA_Redaction_Rejected: 'qa',
  Employee_Companion_Data_Erased: 'qa',
} as const satisfies Record<AdminAuditAction, Exclude<ActionGroup, 'all'>>;

// Safe lookup — at runtime `e.action` is just a string, so a server ahead of
// the client could deliver an action key that isn't in the map. Returning
// `undefined` keeps such entries visible under "All" without bucketing them
// into the wrong group.
function groupFor(action: string): Exclude<ActionGroup, 'all'> | undefined {
  return (ACTION_TO_GROUP as Record<string, Exclude<ActionGroup, 'all'> | undefined>)[action];
}

export function AdminAuditLog() {
  const audit = useQuery({
    queryKey: ['admin', 'audit-log'],
    queryFn: () => api.get<{ entries: AdminAuditEntry[] }>('/api/admin/audit-log?limit=200'),
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const group = (searchParams.get('group') ?? 'all') as ActionGroup;
  const queryText = searchParams.get('q') ?? '';

  function setGroup(next: ActionGroup) {
    const sp = new URLSearchParams(searchParams);
    if (next === 'all') sp.delete('group');
    else sp.set('group', next);
    setSearchParams(sp, { replace: true });
  }
  function setQuery(next: string) {
    const sp = new URLSearchParams(searchParams);
    if (!next) sp.delete('q');
    else sp.set('q', next);
    setSearchParams(sp, { replace: true });
  }

  const filtered = useMemo(() => {
    const all = audit.data?.entries ?? [];
    const q = queryText.trim().toLowerCase();
    return all.filter((e) => {
      // Guard against deployment skew: the server may emit an action the
      // client's `AdminAuditAction` union doesn't know about yet. In that case
      // the entry only shows under "All" (it's still real audit data) and
      // doesn't get force-bucketed into a wrong group.
      if (group !== 'all' && groupFor(e.action) !== group) return false;
      if (!q) return true;
      const haystacks = [e.action, e.actorAdminName ?? '', e.targetOrgName ?? ''];
      return haystacks.some((h) => h.toLowerCase().includes(q));
    });
  }, [audit.data, group, queryText]);

  return (
    <section className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Audit log</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          State-changing actions across all tenants (latest 200). Passive reads (org list,
          drill-ins) go through the standard observability pipeline, not here.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-1 rounded-md border border-border bg-background p-1">
          {(['all', 'org', 'prompt', 'policy', 'cgc', 'admin', 'governance', 'qa'] as const).map(
            (g) => (
              <FilterChip
                key={g}
                active={group === g}
                onClick={() => setGroup(g)}
                label={GROUP_LABELS[g]}
                count={
                  g === 'all'
                    ? audit.data?.entries.length
                    : audit.data?.entries.filter((e) => groupFor(e.action) === g).length
                }
              />
            ),
          )}
        </div>
        <input
          type="search"
          value={queryText}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search action, actor, or org"
          className="min-w-0 flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none sm:max-w-xs"
        />
      </div>

      {audit.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : audit.error ? (
        <p className="text-sm text-traffic-red">Could not load audit log.</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-muted/30 p-6 text-sm text-muted-foreground">
          {audit.data.entries.length === 0
            ? 'No actions logged yet.'
            : 'No entries match the current filters.'}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-muted-foreground">
              <tr>
                <th className="px-4 py-2 font-medium">When</th>
                <th className="px-4 py-2 font-medium">Actor</th>
                <th className="px-4 py-2 font-medium">Action</th>
                <th className="px-4 py-2 font-medium">Target</th>
                <th className="px-4 py-2 font-medium">Payload</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.id} className="border-t border-border align-top">
                  <td className="px-4 py-2 text-muted-foreground">
                    {new Date(e.createdAt).toLocaleString()}
                  </td>
                  <td className="px-4 py-2">
                    {e.actorAdminName ??
                      (e.actorAdminId ? `${e.actorAdminId.slice(0, 8)}…` : 'System')}
                  </td>
                  <td className="px-4 py-2 font-medium">{e.action}</td>
                  <td className="px-4 py-2 text-muted-foreground">
                    {e.targetOrgName ?? (e.targetOrgId ? `${e.targetOrgId.slice(0, 8)}…` : '—')}
                  </td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">
                    {e.payload ? <code>{JSON.stringify(e.payload)}</code> : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const GROUP_LABELS: Record<ActionGroup, string> = {
  all: 'All',
  org: 'Orgs',
  prompt: 'Prompts',
  policy: 'Policies',
  cgc: 'CGC',
  admin: 'Admins',
  governance: 'Governance',
  qa: 'QA',
};

function FilterChip({
  active,
  onClick,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number | undefined;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'rounded px-3 py-1.5 text-xs font-medium transition',
        active
          ? 'bg-foreground text-background'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      ].join(' ')}
    >
      {label}
      {typeof count === 'number' ? (
        <span className={`ml-1.5 ${active ? 'opacity-70' : 'opacity-60'}`}>{count}</span>
      ) : null}
    </button>
  );
}
